import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Modal,
  Divider,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  CircularProgress,
  Switch,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { collection, getDocs, doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../Firebase";
import { normalizeToHMinS } from "../utils/timeFormat";

// Keep same modal style you already use
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 700,
  bgcolor: "white",
  boxShadow: 24,
  p: 3,
  borderRadius: 2,
};

const IOSSwitch = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "200ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#34C759",
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color: theme.palette.mode === "light" ? "#f4f4f4" : "#757575",
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: "#E5E5EA",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 200,
    }),
  },
}));

export default function WorkerClockingHistoryModal({
    open,
    onClose,
    userId,
    sites = [],
    workerProfileImg, 
    emptyImg, 
    t = (x) => x, 
  }) {


 const [clockingHistoryLoading, setClockingHistoryLoading] = useState(false);
 const [clockingHistory, setClockingHistory] = useState([]);
 const [selectedUserDetails, setSelectedUserDetails] = useState(null);
 const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
 const [activeUserId, setActiveUserId] = useState(null); 
 const [smsOptInLoading, setSmsOptInLoading] = useState(false);


  // Fetch the same way your AdminClockingReport does
  const fetchUserClockingHistory = async (uid) => {
    try {
      const historyRef = collection(db, "users", uid, "clocking history");
      const snapshot = await getDocs(historyRef);
      const entries = snapshot.docs.map((d) => d.data());

      const sortedEntries = entries.sort((a, b) => new Date(b.date) - new Date(a.date));
      setClockingHistory(sortedEntries);

      const userSnap = await getDoc(doc(db, "users", uid));
      if (userSnap.exists()) {
        setSelectedUserDetails({ id: uid, ...userSnap.data() });
      } else {
        setSelectedUserDetails(null);
      }
    } catch (error) {
      console.error("Error fetching clocking history or user data:", error);
    } finally {
    setClockingHistoryLoading(false);
    setHasFetchedOnce(true); 
    setActiveUserId(uid);
    }

  };

  // When opened (or userId changes), load the same modal content
    useEffect(() => {
    if (!open || !userId) return;

    setActiveUserId(null);
    setHasFetchedOnce(false);

    setClockingHistoryLoading(true);
    setClockingHistory([]);
    setSelectedUserDetails(null);

    fetchUserClockingHistory(userId);
    }, [open, userId]);


    // Robust time parser -> always returns "HH:MM:SS" or null
    const toTimeHHMMSS = (timeStr) => {
      if (!timeStr) return null;

      const s = String(timeStr).trim();

      // Format: "06 h 45 min 56 s" (seconds optional)
      const hmsMatch = s.match(/(\d{1,2})\s*h\s*(\d{1,2})\s*min(?:\s*(\d{1,2})\s*s)?/i);
      if (hmsMatch) {
        const hh = String(hmsMatch[1]).padStart(2, "0");
        const mm = String(hmsMatch[2]).padStart(2, "0");
        const ss = String(hmsMatch[3] ?? "0").padStart(2, "0");
        return `${hh}:${mm}:${ss}`;
      }

      // Format: "HH:MM" or "HH:MM:SS"
      if (!s.includes("AM") && !s.includes("PM") && s.includes(":")) {
        const parts = s.split(":").map((p) => p.trim());
        if (parts.length === 2) {
          const [hh, mm] = parts;
          return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`;
        }
        if (parts.length === 3) {
          const [hh, mm, ss] = parts;
          return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
        }
      }

      // Format: "6:45 AM" / "06:45 PM"
      if (s.includes("AM") || s.includes("PM")) {
        const [time, mod] = s.split(" ");
        let [h, m] = time.split(":");
        if (h === "12") h = "00";
        if (mod === "PM") h = String(parseInt(h, 10) + 12);
        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
      }

      return null;
    };

    const hhmmssToDate = (hhmmss) => new Date(`2000-01-01T${hhmmss}`);


  // Group & sort exactly like your existing code
  const sortedGrouped = useMemo(() => {
    const grouped = {};
    clockingHistory.forEach((entry) => {
      const { date, type } = entry;
      if (!grouped[date]) grouped[date] = { date, clockIn: null, clockOut: null };
      if (type === "Clock In") grouped[date].clockIn = entry;
      if (type === "Clock Out") grouped[date].clockOut = entry;
    });

    const parse = (d) => {
      const [day, month, year] = d.split("/");
      return new Date(`${year}-${month}-${day}`);
    };

    return Object.values(grouped).sort((a, b) => parse(b.date) - parse(a.date));
  }, [clockingHistory]);

  const isDetailsForCurrentUser = open && userId && selectedUserDetails?.id === userId;
  const isDataForCurrentUser = open && userId && activeUserId === userId;

  const handleSmsOptInChange = async (e) => {
    if (!selectedUserDetails?.id) return;
    const nextValue = e.target.checked;
    setSmsOptInLoading(true);
    try {
      const userRef = doc(db, "users", selectedUserDetails.id);
      await updateDoc(userRef, { smsOptIn: nextValue });
      setSelectedUserDetails((prev) => ({ ...prev, smsOptIn: nextValue }));
    } catch (error) {
      console.error("Error updating smsOptIn:", error);
    } finally {
      setSmsOptInLoading(false);
    }
  };


  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{ ...modalStyle, width: "85%", maxHeight: "75vh", overflowY: "auto", position: "relative" }}>
        {/* Close */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "#999",
            "&:hover": { color: "#c93d3d" },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h5" fontWeight="bold" mb={2}>
          {t("Historique des pointages du jour")}
        </Typography>

        {isDetailsForCurrentUser ? (
        <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            alignItems="flex-start"
            gap={2}
            mb={1}
            p={2}
            sx={{
            border: "1px solid #ddd",
            borderRadius: 2,
            backgroundColor: "#f8f8f8",
            }}
        >
            {/* Left: user info */}
            <Box flex={1} display="flex" flexDirection="column" gap={1}>
            <Typography variant="h5" fontWeight="bold">
                {selectedUserDetails.fullName}
            </Typography>

            <Typography variant="body1">
                <strong>{t("Email")}:</strong> {selectedUserDetails.email || "—"}
            </Typography>

            <Typography variant="body1">
                <strong>{t("Phone")}:</strong> {selectedUserDetails.phone || "—"}
            </Typography>

            <Typography variant="body1">
                <strong>{t("Status")}:</strong>{" "}
                {selectedUserDetails.isClockedIn ? t("Clocked In") : t("Clocked Out")}
            </Typography>

            <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body1">
                <strong>{t("SMS Reminder")}:</strong>
            </Typography>
            <IOSSwitch
              checked={Boolean(selectedUserDetails?.smsOptIn)}
              onChange={handleSmsOptInChange}
              disabled={smsOptInLoading}
            />
            </Box>

            <Box display="flex" alignItems="center" gap={2} mt={1}>
                <Typography variant="body1">
                <strong>{t("Assigned Site")}:</strong>
                </Typography>

                <TextField
                select
                size="small"
                value={selectedUserDetails.assignedSiteId || ""}
                onChange={async (e) => {
                    const newSiteId = e.target.value;
                    const userRef = doc(db, "users", selectedUserDetails.id);
                    await updateDoc(userRef, { assignedSiteId: newSiteId });

                    setSelectedUserDetails((prev) => ({ ...prev, assignedSiteId: newSiteId }));
                }}
                SelectProps={{ native: true }}
                sx={{ backgroundColor: "white", minWidth: 200 }}
                >
                <option value="">-- {t("Select Site")} --</option>
                {sites.map((site) => (
                    <option key={site.id} value={site.id}>
                    {site.name}
                    </option>
                ))}
                </TextField>
            </Box>
            </Box>

            {/* Right: image */}
            <Box
            sx={{
                width: 300,
                height: 200,
                borderRadius: 2,
                overflow: "hidden",
                flexShrink: 0,
                mt: { xs: 2, sm: 0 },
                backgroundColor: "#fff",
                border: "1px solid #e5e7eb",
            }}
            >
            {workerProfileImg ? (
                <img
                src={workerProfileImg}
                alt={t("Worker")}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
            ) : (
                <Box sx={{ width: "100%", height: "100%", display: "grid", placeItems: "center", color: "#6b7280" }}>
                {t("No image")}
                </Box>
            )}
            </Box>
        </Box>
        ) : (
        <Box
            sx={{
            border: "1px solid #ddd",
            borderRadius: 2,
            backgroundColor: "#f8f8f8",
            p: 2,
            mb: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 120,
            }}
        >
            <CircularProgress size={26} />
        </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {(!isDataForCurrentUser || !hasFetchedOnce || clockingHistoryLoading) ? ( // ✅ KEY CHANGE
            <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={28} />
            </Box>
            ) : sortedGrouped.length === 0 ? (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py={5}
                sx={{ border: "1px solid #ddd", borderRadius: 2 }}
            >
                <Typography variant="h6" color="text.secondary" mt={2} sx={{ color: "gray" }}>
                {t("No clocking history available for this user.")}
                </Typography>

                {emptyImg ? (
                <img src={emptyImg} alt={t("No history")} style={{ width: 180, opacity: 0.6 }} />
                ) : null}
            </Box>
            ) : (
          <Table sx={{ border: "1px solid #ddd", borderRadius: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t("Date")}</TableCell>
                <TableCell>{t("Clock In")}</TableCell>
                <TableCell>{t("Clock Out")}</TableCell>
                <TableCell>{t("Total Worked")}</TableCell>
                <TableCell>{t("Location")}</TableCell>
                <TableCell>{t("Note")}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedGrouped.map(({ date, clockIn, clockOut }, index) => {
                let totalWorked = t("—");

                if (clockIn?.time && clockOut?.time) {
                  const inHHMMSS = toTimeHHMMSS(clockIn.time);
                  const outHHMMSS = toTimeHHMMSS(clockOut.time);

                  if (inHHMMSS && outHHMMSS) {
                    const start = hhmmssToDate(inHHMMSS);
                    const end = hhmmssToDate(outHHMMSS);

                    const diffMins = (end - start) / 60000;

                    // Guard against invalid or negative durations
                    if (Number.isFinite(diffMins) && diffMins >= 0) {
                      const h = Math.floor(diffMins / 60);
                      const m = Math.floor(diffMins % 60);
                      totalWorked = `${h}h ${m}m`;
                    } else {
                      totalWorked = t("—");
                    }
                  } else {
                    totalWorked = t("—");
                  }
                }

                return (
                  <TableRow key={index}>
                    <TableCell>{date}</TableCell>
                    <TableCell>{normalizeToHMinS(clockIn?.time) || t("—")}</TableCell>
                    <TableCell>
                    <Typography
                      sx={{
                        color: clockOut?.note === "Pointage effectué par l’administrateur" ? "#c33438ff" : "inherit",
                      }}
                    >
                      {normalizeToHMinS(clockOut?.time) || t("—")}
                    </Typography>
                   </TableCell>
                    <TableCell>{totalWorked}</TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                        {clockIn?.location && (
                          <>
                            <b>{t("In")}:</b> {clockIn.location}
                            {"\n"}
                          </>
                        )}
                        {clockOut?.location && (
                          <>
                            <b>{t("Out")}:</b> {clockOut.location}
                          </>
                        )}
                        {!clockIn?.location && !clockOut?.location && t("—")}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                        {clockIn?.note && clockIn.note !== "—" && (
                          <>
                            <b>{t("In")}:</b> {clockIn.note}
                            {"\n"}
                          </>
                        )}
                        {clockOut?.note && clockOut.note !== "—" && (
                          <>
                            <b>{t("Out")}:</b> {clockOut.note}
                          </>
                        )}
                        {(!clockIn?.note || clockIn.note === "—") &&
                          (!clockOut?.note || clockOut.note === "—") &&
                          t("—")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>
    </Modal>
  );
}
